import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, rupees } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, MapPin, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Delivery() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/delivery/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const deliver = async (id) => {
    if (!confirm("Mark this order as delivered?")) return;
    await api.post(`/delivery/orders/${id}/deliver`);
    toast.success("Delivered!"); load();
  };

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header/>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="label-caps text-stone-500">Runs</div>
        <h1 className="font-serif-display text-3xl text-[#1A362B] tracking-tight mt-1 mb-8">Deliveries assigned to you</h1>

        {orders.length === 0 ? (
          <div className="border border-dashed border-stone-300 rounded-md p-12 text-center">
            <Truck className="w-8 h-8 mx-auto text-stone-400"/>
            <p className="font-serif-display text-2xl text-[#1A362B] mt-4">No pending deliveries</p>
            <p className="text-stone-500 text-sm mt-2">Assigned orders will appear here.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {orders.map((o) => (
              <div key={o.id} className="p-5 border border-stone-200 rounded-md bg-white" data-testid={`delivery-order-${o.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-mono-num text-sm text-stone-600">#{o.order_no}</div>
                    <Badge className={`status-${o.status} rounded-sm border-0 capitalize mt-1`}>{o.status}</Badge>
                  </div>
                  <div className="font-mono-num text-xl text-[#1A362B]">{rupees(o.total)}</div>
                </div>
                <div className="text-sm text-[#1A362B] font-medium">{o.customer_name}</div>
                <div className="mt-3 text-sm text-stone-600 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0"/>
                  <div>
                    {o.address.line1}{o.address.line2 ? `, ${o.address.line2}` : ""}<br/>
                    {o.address.city}, {o.address.state} - {o.address.pincode}
                  </div>
                </div>
                <div className="mt-2 text-sm text-stone-600 flex items-center gap-2">
                  <Phone className="w-4 h-4"/> <a href={`tel:${o.address.phone}`} className="hover:text-[#1A362B]">{o.address.phone}</a>
                </div>
                <div className="mt-4 text-xs text-stone-500 uppercase tracking-wider">
                  Payment: <span className="text-[#D96C4A] font-semibold">{o.payment_method === "cod" ? `Collect ${rupees(o.total)} CASH` : "Prepaid"}</span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Link to={`/orders/${o.id}`} className="w-full">
                    <Button variant="outline" className="w-full rounded-sm" data-testid={`view-${o.id}`}>View items</Button>
                  </Link>
                  <Button onClick={() => deliver(o.id)} className="rounded-sm bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6]" data-testid={`deliver-${o.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1"/> Delivered
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
